import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppInput from "../../components/Core/AppInput/AppInput";
import Flex from "../../components/Layout/Flex";
import { DocumentFileBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import Button from "../../components/Core/Button/Button";
import FileImporter from "../../components/Shared/FileImporter/FileImporter";
import AppText, { FormLabel } from "../../components/Core/AppText/AppText";
import Checkbox from "../../components/Core/Checkbox/Checkbox";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { useCompetence, useUploadDocuments } from "../../networking/organisation.hooks";
import { Alert, AlertDescription, useToast } from "@chakra-ui/react";
import { useMediaStore } from "../../stores/media.store";

const DocumentUploadPage: React.FC = () => {
	const { t } = useTranslation();
	const mediaStore = useMediaStore();
	const toast = useToast();
	const navigate = useNavigate();
	const { document, documentLoading } = mediaStore;

	const { data: competenceList, isLoading: competenceLoading } = useCompetence(true);

	const {
		mutate: publishDoc,
		isLoading: publishing,
		isSuccess: documentPublished,
		isError: documentPublishError,
	} = useUploadDocuments();

	const [dropZoneError, setDropZoneError] = useState<string>("");
	const [selectedBranches, setSelectedBranches] = useState<Array<string>>([]);
	const [allBranches, setAllBranches] = useState<boolean>(false);
	const [title, setTitle] = useState<string>("");

	function publishDocument() {
		if ((selectedBranches.length > 0 || allBranches) && document && title) {
			const params: {
				branches?: Array<string>;
				mimeType: string;
				title: string;
				type: string;
				url: string;
			} = {
				mimeType: document?.mimeType || "",
				title: title,
				type: "document",
				url: document?.url || "",
			};

			publishDoc(params);
		} else {
			toast({
				id: "documentPublish validation",
				render: () => (
					<Alert status="warning" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_FILL_IN_MESSAGE")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "bottom-right",
				duration: 2 * 1000,
			});
		}
	}

	useEffect(() => {
		if (documentPublished) {
			toast({
				id: "documentPublished",
				render: () => (
					<Alert status="success" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_PUBLISHED_MESSAGE")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "top",
				duration: 3 * 1000,
				onCloseComplete: () => handleRefresh(),
			});
		}
		if (documentPublishError) {
			toast({
				id: "documentPublished error",
				render: () => (
					<Alert status="error" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_UPDATE_MESSAGE_ERROR")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "top",
				duration: 3 * 1000,
			});
		}
	}, [documentPublished, documentPublishError]);

	const handleRefresh = () => {
		navigate(routes.DOCUMENTS);
	};

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={DocumentFileBreadcrumbs} />
			<Flex direction="column" width="50%">
				<AppText margin="0 0 10px 0" size="m">
					{"Fil" + "*"}&nbsp;
				</AppText>
				<Flex margin="0 0 35px 0">
					<FileImporter dropZoneError={dropZoneError} />
					{documentLoading && (
						<div
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								background: "rgba(255, 255, 255, 0.8)",
								zIndex: 1,
							}}
						>
							<LoadingIndicator />
						</div>
					)}
				</Flex>

				<Flex direction="column">
					<AppInput
						label={t("TITLE") + "*"}
						margin="0.5rem 0"
						placeholder={t("TITLE")}
						value={title}
						onChange={(e: any) => setTitle(e.target.value)}
					/>
				</Flex>
				<Flex direction="column" margin="0 1rem 0 0">
					<FormLabel>{t("showConsultants")}</FormLabel>
					<Checkbox
						margin="0.5rem 0"
						label="Alla branscher"
						isChecked={allBranches}
						onChange={(e: any) => {
							setAllBranches(e.target.checked);
							if (e.target.checked) {
								setSelectedBranches([]);
							}
						}}
					/>
				</Flex>
				<Flex direction="column" margin="0 1rem 0 1rem">
					{competenceLoading ? (
						<LoadingIndicator />
					) : (
						competenceList?.competences &&
						Array.isArray(competenceList.competences) &&
						competenceList.competences.map((compt) => {
							return (
								<Checkbox
									margin="0.5rem 0"
									label={compt.name.sv}
									value={compt.key}
									isChecked={selectedBranches.includes(compt.key)}
									disabled={allBranches}
									onChange={(e: any) => {
										e.target.checked
											? setSelectedBranches([...selectedBranches, compt.key])
											: setSelectedBranches(
													selectedBranches.filter((branch) => branch !== compt.key)
											  );
									}}
								/>
							);
						})
					)}
				</Flex>
			</Flex>
			<Flex direction="row" width="100%" justifyContent="center" margin="2rem 10rem 0 0rem">
				<Button width="auto" loading={publishing} onClick={() => publishDocument()}>
					{t("publishDocuments")}
				</Button>
			</Flex>
		</div>
	);
};

export default DocumentUploadPage;
